import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Trophy } from 'lucide-react';
import type { LoyaltyPointsRangesToken } from '@sleed/bizcommerce-api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { SystemGrid, type SystemGridColumn } from '~/components/system/grid/system-grid';
import { serverMessage } from '~/lib/api/server-error';
import { formatCurrency } from '~/lib/dashboard/format';
import { formatDate } from '~/lib/format-date';
import { useLoyaltyRangesList, useLoyaltyRangeMutations } from '~/hooks/use-loyalty-ranges';
import { tokenToForm, type LoyaltyRangeFormValues } from '~/lib/schemas/loyalty-range';
import { LoyaltyRangeEditDialog } from './range-edit-dialog';

const KNOWN_DELETE_MESSAGES = new Set(['already_cancelled', 'expired_successfully', 'deleted_sucessfully']);

/** Loyalty points ranges (Εύρος πόντων) CRUD surface — parity with the legacy admin grid. */
export function LoyaltyRangesGridView() {
	const { t } = useTranslation();
	const { data, isLoading, isError, refetch } = useLoyaltyRangesList();
	const { remove } = useLoyaltyRangeMutations();

	const [editOpen, setEditOpen] = useState(false);
	const [editing, setEditing] = useState<LoyaltyRangeFormValues | null>(null);
	const [deleteRow, setDeleteRow] = useState<LoyaltyPointsRangesToken | null>(null);

	const columns = useMemo<SystemGridColumn<LoyaltyPointsRangesToken>[]>(
		() => [
			{
				key: 'points',
				header: t('loyalty.ranges.columns.points'),
				width: 'minmax(0,1fr)',
				align: 'right',
				sortValue: o => o.pnt ?? 0,
				exportValue: o => o.pnt ?? 0,
				render: o => <span className="text-sm font-medium tabular-nums text-foreground">{o.pnt ?? 0}</span>
			},
			{
				key: 'discount',
				header: t('loyalty.ranges.columns.discount'),
				width: '160px',
				align: 'right',
				sortValue: o => o.amn ?? 0,
				exportValue: o => o.amn ?? 0,
				render: o => <span className="text-sm tabular-nums text-foreground-muted">{formatCurrency(o.amn ?? 0)}</span>
			},
			{
				key: 'minimumAmount',
				header: t('loyalty.ranges.columns.minimumAmount'),
				width: '160px',
				align: 'right',
				sortValue: o => o.mamn ?? 0,
				exportValue: o => o.mamn ?? '',
				render: o => <span className="text-sm tabular-nums text-foreground-muted">{o.mamn == null ? '—' : formatCurrency(o.mamn)}</span>
			},
			{
				key: 'activeFrom',
				header: t('loyalty.ranges.columns.activeFrom'),
				width: '150px',
				sortValue: o => (o.afrom ? String(o.afrom) : ''),
				exportValue: o => (o.afrom ? String(o.afrom) : ''),
				render: o => <span className="text-sm text-foreground-muted">{formatDate(o.afrom)}</span>
			},
			{
				key: 'activeTo',
				header: t('loyalty.ranges.columns.activeTo'),
				width: '150px',
				sortValue: o => (o.ato ? String(o.ato) : ''),
				exportValue: o => (o.ato ? String(o.ato) : ''),
				render: o => <span className="text-sm text-foreground-muted">{formatDate(o.ato)}</span>
			},
			{
				key: 'updated',
				header: t('loyalty.ranges.columns.updated'),
				width: '150px',
				defaultVisible: false,
				sortValue: o => (o.crt ? String(o.crt) : ''),
				exportValue: o => (o.crt ? String(o.crt) : ''),
				render: o => <span className="text-sm text-foreground-muted">{formatDate(o.crt)}</span>
			}
		],
		[t]
	);

	const confirmDelete = () => {
		if (!deleteRow?.id) return;
		remove.mutate(deleteRow.id, {
			onSuccess: msg => {
				const code = String(msg ?? '');
				toast.success(KNOWN_DELETE_MESSAGES.has(code) ? t(`loyalty.ranges.deleteResult.${code}`) : t('common.delete'));
				setDeleteRow(null);
			},
			onError: (err: unknown) => toast.error(serverMessage(err))
		});
	};

	return (
		<>
			<SystemGrid<LoyaltyPointsRangesToken>
				gridId="loyalty-ranges"
				title={t('loyalty.ranges.title')}
				rows={data ?? []}
				loading={isLoading}
				error={isError}
				onRetry={() => void refetch()}
				getRowId={o => o.id ?? o.pnt ?? ''}
				columns={columns}
				searchPredicate={(o, q) => `${o.pnt ?? ''} ${o.amn ?? ''} ${o.mamn ?? ''}`.toLowerCase().includes(q)}
				defaultSort={{ key: 'points', dir: 'asc' }}
				emptyIcon={<Trophy size={36} className="text-foreground-disabled" strokeWidth={1.25} />}
				addLabel={t('loyalty.ranges.add')}
				onAdd={() => {
					setEditing(null);
					setEditOpen(true);
				}}
				onEdit={o => {
					setEditing(tokenToForm(o));
					setEditOpen(true);
				}}
				onDelete={setDeleteRow}
				exportFilename="loyalty-ranges"
			/>

			<LoyaltyRangeEditDialog open={editOpen} onOpenChange={setEditOpen} initial={editing} />

			<Dialog open={Boolean(deleteRow)} onOpenChange={open => !open && setDeleteRow(null)}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t('loyalty.ranges.deleteTitle')}</DialogTitle>
						<DialogDescription>{t('loyalty.ranges.deleteBody', { points: deleteRow?.pnt ?? '' })}</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="ghost" onClick={() => setDeleteRow(null)}>
							{t('common.cancel')}
						</Button>
						<Button variant="destructive" onClick={confirmDelete} disabled={remove.isPending}>
							{t('common.delete')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}
